import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, SparkIcon, TagColored } from '@spark';
import GenAiModalShell from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/GenAiModalShell';
import DefineTaskStep from '@/features/datasets/components/DataCollection/GenerateDataWithAI/steps/DefineTaskStep';
import RecordsConfigStep from '@/features/datasets/components/DataCollection/GenerateDataWithAI/steps/RecordsConfigStep';
import ReviewStep from '@/features/datasets/components/DataCollection/GenerateDataWithAI/steps/ReviewStep';
import { createDefaultSettings } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/AdvancedSettingsSection';
import useDatasetNameField from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/useDatasetNameField';
import {
    generationSessionConfig,
    nerExampleRowsFromConfig,
    settingsFromConfig,
} from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/generationSessionPrefill';
import useGenerationSessions from '@/features/datasets/components/DataCollection/GenerateDataWithAI/useGenerationSessions';
import useGenerationTargets from '@/features/datasets/components/DataCollection/GenerateDataWithAI/useGenerationTargets';
import useRecordsState from '@/features/datasets/components/DataCollection/GenerateDataWithAI/useRecordsState';
import { formatRecordsBanner } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/flows/ClassificationFlow';
import {
    EMPTY_NER_DATASET_BANNER_TEXT,
    NER_EXAMPLES_INSTRUCTION,
} from '@/features/datasets/components/DataCollection/GenerateDataWithAI/constants';
import { reportRequiredFieldsError } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/requiredFieldsError';
import NerExampleEditor from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/NerExampleEditor';
import {
    stripEntitiesOfType,
    toTrimmedNerExample,
} from '@/features/datasets/components/DataCollection/GenerateDataWithAI/shared/nerExampleSpans';
import { getSparkTagVariant } from '@/features/datasets/components/DataSelection/sparkLabelColors';
import { fetchGenerationDatasetNameSuggestion } from '@/features/datasets/utils/aiGenerationDataset';
import { targetNamesFromConfig } from '@/features/datasets/utils/datasetSourceConfig';
import { retrievalTaskTypeLabel, sessionOptionTitle } from '@/features/datasets/utils/retrievalAgentDataset';

/**
 * Entity extraction flow (Figma ner frames): shared Define Task (generation
 * goal + description, Advanced settings) → Records to generate + per-entity
 * description blocks and tagged example texts with entity tags (mirrors the
 * Retrieval Agent's Optional Settings step) → Review & Generate → Start
 * Generate. Steps after Define Task show a bottom-left "Back" control.
 */
const NerFlow = ({ open, onClose, onGenerate, usecase, warningBanner, disabled = false }) => {
    const [step, setStep] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [goal, setGoal] = useState('');
    const [description, setDescription] = useState('');
    const [goalError, setGoalError] = useState(false);
    const [descriptionError, setDescriptionError] = useState(false);
    const [examples, setExamples] = useState([]);
    const [settings, setSettings] = useState(createDefaultSettings());
    // null until the user edits the tags; falls back to the usecase's entity types.
    const [editedNames, setEditedNames] = useState(null);
    const [addingTag, setAddingTag] = useState(false);
    const [newTagName, setNewTagName] = useState('');
    const [footerError, setFooterError] = useState('');
    const [loadedSessionTitle, setLoadedSessionTitle] = useState(undefined);

    const sessions = useGenerationSessions(usecase?._id, open);
    const { datasetName, onDatasetNameChange, datasetNameError, validateDatasetName } = useDatasetNameField({
        open,
        usecaseId: usecase?._id,
        fetchSuggestion: fetchGenerationDatasetNameSuggestion,
    });

    const { targets } = useGenerationTargets(usecase, open);
    const usecaseNames = useMemo(() => targets.map((target) => target.name), [targets]);
    const entityNames = editedNames ?? usecaseNames;
    const countsByName = useMemo(() => {
        const counts = Object.fromEntries(entityNames.map((name) => [name, 0]));
        examples.forEach((row) => {
            (row.entities || []).forEach((entity) => {
                if (counts[entity.type] != null) counts[entity.type] += 1;
            });
        });
        return counts;
    }, [examples, entityNames]);
    const records = useRecordsState(entityNames);

    const reset = () => {
        setStep(0);
        setGoal('');
        setDescription('');
        setGoalError(false);
        setDescriptionError(false);
        setExamples([]);
        setSettings(createDefaultSettings());
        setEditedNames(null);
        setAddingTag(false);
        setNewTagName('');
        setFooterError('');
        setLoadedSessionTitle(undefined);
    };

    /** Prefill the wizard from a past generation configuration (Dataset Name keeps its fresh default). */
    const applySession = (session) => {
        const config = generationSessionConfig(session);
        if (!config) return;
        setLoadedSessionTitle(sessionOptionTitle(session));
        setGoal(config.goal);
        setGoalError(false);
        setDescription(config.description);
        setDescriptionError(false);
        setSettings(settingsFromConfig(config));
        setExamples(nerExampleRowsFromConfig(config));
        const names = targetNamesFromConfig(config);
        if (names.length) setEditedNames(names);
        records.prefill({
            names: names.length ? names : entityNames,
            totalRecords: config.datasetSize,
            counts: config.recordCounts,
            configs: config.targetConfigs,
        });
        setFooterError('');
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const removeTag = (name) => {
        setEditedNames(entityNames.filter((item) => item !== name));
        setExamples((prev) =>
            prev.map((row) => ({ ...row, entities: stripEntitiesOfType(row.entities, name) }))
        );
    };

    const commitNewTag = () => {
        const trimmed = newTagName.trim();
        setAddingTag(false);
        setNewTagName('');
        if (!trimmed) return;
        if (entityNames.some((name) => name.toLowerCase() === trimmed.toLowerCase())) return;
        setEditedNames([...entityNames, trimmed]);
    };

    const patchExample = (index, next) => {
        setExamples((prev) => prev.map((row, i) => (i === index ? next : row)));
        setFooterError('');
    };

    const removeExample = (index) => {
        setExamples((prev) => prev.filter((_, i) => i !== index));
        setFooterError('');
    };

    const validateStep0 = () => {
        const nextGoalError = !goal.trim();
        const nextDescriptionError = !description.trim();
        const datasetNameValid = validateDatasetName();
        setGoalError(nextGoalError);
        setDescriptionError(nextDescriptionError);
        return !nextGoalError && !nextDescriptionError && datasetNameValid;
    };

    /** Examples are optional (aligned with the Retrieval Agent). */
    const validateStep1 = () => !records.totalRecordsError && entityNames.length > 0;

    const handlePrimary = async () => {
        if (step === 0) {
            if (validateStep0()) {
                setFooterError('');
                setStep(1);
            } else {
                setFooterError(reportRequiredFieldsError());
            }
            return;
        }
        if (step === 1) {
            if (validateStep1()) {
                setFooterError('');
                setStep(2);
            } else {
                setFooterError(reportRequiredFieldsError());
            }
            return;
        }
        try {
            setIsSubmitting(true);
            await onGenerate({
                datasetName: datasetName.trim(),
                goal: goal.trim(),
                description: description.trim(),
                examples: examples
                    .map((row) => toTrimmedNerExample(row.text, row.entities))
                    .filter(Boolean)
                    .map((row) => ({
                        kind: 'ner',
                        text: row.text,
                        response: { kind: 'ner', entities: row.entities },
                    })),
                entityNames,
                totalRecords: records.totalRecords,
                recordCounts: records.counts,
                targetConfigs: records.configs,
                settings,
            });
            handleClose();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <GenAiModalShell
            open={open}
            onClose={handleClose}
            title="Generate data with AI"
            subtitle="Define what the dataset should teach, then configure the labels and examples that will guide generation."
            primaryLabel={step === 2 ? 'Start Generate' : 'Next'}
            onPrimary={handlePrimary}
            primaryDisabled={disabled}
            primaryDisableWhenReadOnly={step === 2}
            isSubmitting={isSubmitting}
            showBack={step > 0}
            onBack={() => {
                setFooterError('');
                setStep(step - 1);
            }}
            footerError={footerError}
        >
            {warningBanner}
            {step === 0 ? (
                <DefineTaskStep
                    bannerText={formatRecordsBanner(targets, EMPTY_NER_DATASET_BANNER_TEXT)}
                    datasetName={datasetName}
                    onDatasetNameChange={onDatasetNameChange}
                    datasetNameError={datasetNameError}
                    sessions={sessions}
                    loadedSessionTitle={loadedSessionTitle}
                    onLoadSession={applySession}
                    goal={goal}
                    description={description}
                    goalError={goalError}
                    descriptionError={descriptionError}
                    onGoalChange={(value) => {
                        setGoal(value);
                        setGoalError(false);
                        setFooterError('');
                    }}
                    onDescriptionChange={(value) => {
                        setDescription(value);
                        setDescriptionError(false);
                        setFooterError('');
                    }}
                    settings={settings}
                    onSettingsChange={setSettings}
                    showTextLengthOptionHelp
                />
            ) : null}
            {step === 1 ? (
                <>
                    <RecordsConfigStep
                        variant="ner"
                        targetNames={entityNames}
                        totalRecords={records.totalRecords}
                        onTotalRecordsChange={records.onTotalRecordsChange}
                        counts={records.counts}
                        onCountChange={records.onCountChange}
                        configs={records.configs}
                        onConfigChange={records.onConfigChange}
                        onRemoveTarget={removeTag}
                        totalRecordsError={records.totalRecordsError}
                    />
                    <div className="gdai-section">
                        <div className="gdai-section-heading fs-unmask">Examples</div>
                        <p className="gdai-examples-instruction fs-unmask">{NER_EXAMPLES_INSTRUCTION}</p>
                        {examples.map((row, index) => (
                            // eslint-disable-next-line react/no-array-index-key
                            <div key={index} className="gdai-section">
                                <div className="gdai-label-header">
                                    <span className="fs-unmask">{`Example (${index + 1})`}</span>
                                    <button
                                        type="button"
                                        className="gdai-icon-button"
                                        aria-label={`Remove example ${index + 1}`}
                                        onClick={() => removeExample(index)}
                                    >
                                        <SparkIcon slug="trash" size="small" />
                                    </button>
                                </div>
                                <NerExampleEditor
                                    placeholder="Type or paste example text"
                                    text={row.text}
                                    entities={row.entities}
                                    entityNames={entityNames}
                                    onChange={(next) => patchExample(index, next)}
                                    height={110}
                                    disabled={disabled}
                                />
                            </div>
                        ))}
                        <div>
                            <Button
                                variant="secondary"
                                size="tiny"
                                startIcon={<SparkIcon slug="plus" size="small" />}
                                onClick={() => setExamples((prev) => [...prev, { text: '', entities: [] }])}
                            >
                                Add Example
                            </Button>
                        </div>
                        <div className="gdai-entity-tags">
                            {entityNames.map((name) => (
                                <TagColored
                                    key={name}
                                    label={`${name} (${(countsByName[name] || 0).toLocaleString('en-US')})`}
                                    variant={getSparkTagVariant(name, entityNames)}
                                    showSuffixIcon
                                    onDelete={() => removeTag(name)}
                                />
                            ))}
                            {addingTag ? (
                                <input
                                    className="gdai-entity-tags__input"
                                    value={newTagName}
                                    autoFocus
                                    placeholder="Tag name"
                                    onChange={(event) => setNewTagName(event.target.value)}
                                    onBlur={commitNewTag}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') commitNewTag();
                                        if (event.key === 'Escape') {
                                            setAddingTag(false);
                                            setNewTagName('');
                                        }
                                    }}
                                />
                            ) : (
                                <Button
                                    variant="secondary"
                                    size="tiny"
                                    startIcon={<SparkIcon slug="plus" size="small" />}
                                    onClick={() => setAddingTag(true)}
                                >
                                    Add New Tag
                                </Button>
                            )}
                        </div>
                    </div>
                </>
            ) : null}
            {step === 2 ? (
                <ReviewStep
                    goal={goal.trim()}
                    customGuidelines={description.trim()}
                    languages={settings.languages}
                    taskTypeLabel={retrievalTaskTypeLabel(usecase)}
                    totalRecords={records.totalRecords}
                />
            ) : null}
        </GenAiModalShell>
    );
};

NerFlow.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onGenerate: PropTypes.func.isRequired,
    usecase: PropTypes.object,
    warningBanner: PropTypes.node,
    disabled: PropTypes.bool,
};

export default NerFlow;
